import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePaymentContracts } from 'src/features/contracts/hooks/usePaymentContracts';
import { useCreateContract } from 'src/features/contracts/hooks/useCreateContract';
import type { Contract } from 'src/types/contract.type';
import { useProjectAllChangeOrders } from '../hooks/useProjectAllChangeOrders';
import OverviewCardsV2 from '../components/OverviewCardsV2';
import CashflowChart from 'src/features/payments/components/CashflowChart';
import { usePaymentCashflow } from 'src/features/payments/hooks/usePaymentCashflow';
import ContractList from 'src/features/contracts/components/ContractList';
import ChangeOrderList from 'src/features/contracts/components/changeOrders/ChangeOrderList';
import ContractEditorModal from 'src/features/contracts/components/modals/ContractEditorModal';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

export default function ContractsPageV2() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState<'contracts' | 'co'>('contracts');
    const [addModalConfig, setAddModalConfig] = useState<{ open: boolean; initial?: Partial<Contract> }>({ open: false });

    // Permissions
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditContract = useMemo(() => !accessLoading && hasPerm(access?.permissions, 'contract.edit'), [accessLoading, access]);

    // Data
    const { data: contracts = [], isLoading: loadingContracts, refetch: refetchContracts } = usePaymentContracts(projectId ?? '');
    const { data: allCOs = [], isLoading: loadingCOs } = useProjectAllChangeOrders(projectId ?? '');

    const cashflow = usePaymentCashflow(projectId ?? '', contracts);

    const createContractMut = useCreateContract(projectId ?? '');

    const clientContracts = useMemo(() => contracts.filter((c) => c.direction === 'CLIENT'), [contracts]);
    const subconContracts = useMemo(() => contracts.filter((c) => c.direction === 'SUBCON'), [contracts]);

    if (!projectId) return null;

    // Header Actions
    const handleNewContract = (direction?: 'CLIENT' | 'SUBCON') => {
        setAddModalConfig({ 
            open: true, 
            initial: direction ? { direction } : undefined 
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Contracts & Change Orders</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage project-wide agreements and variations</p>
                </div>
                <div className="flex items-center gap-2">
                </div>
            </div>

            <div className="space-y-6">
                <CashflowChart 
                    data={cashflow.data?.rows ?? []} 
                    currency={cashflow.data?.currency} 
                    title="Project Cashflow" 
                />
                <OverviewCardsV2 contracts={contracts} projectId={projectId} />
            </div>

            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setTab('contracts')}
                        className={`
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-all
                            ${tab === 'contracts' 
                                ? 'border-gray-900 text-gray-900' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                    >
                        Contracts
                        <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            {contracts.length}
                        </span>
                    </button>
                    <button
                        onClick={() => setTab('co')}
                        className={`
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-all
                            ${tab === 'co' 
                                ? 'border-gray-900 text-gray-900' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                    >
                        Project CO Log
                        <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            {allCOs.length}
                        </span>
                    </button>
                </nav>
            </div>

            <div className="mt-4">
                {tab === 'contracts' ? (
                    <div className="space-y-8">
                        {loadingContracts ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            </div>
                        ) : (
                            <>
                                <ContractList
                                    title="Client Contracts"
                                    contracts={clientContracts}
                                    onNewContract={handleNewContract}
                                    canEdit={canEditContract}
                                    // 상세 이동은 URL 패턴에 따라 ContractDetailPageV2로 유도 필요
                                    // 기존 ContractList 컴포넌트가 navigate 동작을 가지고 있음
                                />
                                <ContractList
                                    title="Subcon Contracts"
                                    contracts={subconContracts}
                                    onNewContract={handleNewContract}
                                    canEdit={canEditContract}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border overflow-hidden">
                        <div className="px-4 py-3 border-b bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-900">All Change Orders</h3>
                        </div>
                        <ChangeOrderList
                            rows={allCOs}
                            loading={loadingCOs}
                            onRowClick={(id) => {
                                const co = allCOs.find(x => x.id === id);
                                if (co) {
                                    navigate(`/projects/${projectId}/contracts/${co.contractId}?tab=co`);
                                }
                            }}
                            canEdit={false} // Global log is read-only for now, edits happen in detail page
                        />
                        {allCOs.length === 0 && !loadingCOs && (
                            <div className="py-12 text-center">
                                <p className="text-sm text-gray-500">No change orders found in this project.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ContractEditorModal
                open={addModalConfig.open}
                initial={addModalConfig.initial}
                projectId={projectId}
                onClose={() => setAddModalConfig({ open: false })}
                canEdit={canEditContract}
                onSave={(input) => {
                    createContractMut.mutate(input, {
                        onSuccess: () => {
                            setAddModalConfig({ open: false });
                            void refetchContracts();
                        },
                    });
                }}
            />
        </div>
    );
}
